export type MentionPopoverItem = {
  id: string;
  label: string;
  description?: string;
};

type Props = {
  open: boolean;
  items: MentionPopoverItem[];
  /** id of the currently-selected item — gets the `·` marker. */
  currentId?: string | null;
  /** Keyboard-focused index (for arrow-key navigation). */
  activeIndex?: number;
  onPick: (id: string) => void;
  /** Hover callback so parent can sync activeIndex with mouse. */
  onHover?: (i: number) => void;
  /** Where the popover sits relative to its anchor. */
  placement?: "above" | "below";
  /** "anchor" stretches to the parent (chat input card); "intrinsic" sizes to content. */
  width?: "anchor" | "intrinsic";
  footer?: { label: string; onClick: () => void };
  ariaHidden?: boolean;
};

export function MentionPopover({
  open,
  items,
  currentId = null,
  activeIndex,
  onPick,
  onHover,
  placement = "above",
  width = "anchor",
  footer,
  ariaHidden,
}: Props) {
  return (
    <div
      className="cmd-suggestions-wrap"
      data-open={open ? "true" : "false"}
      data-kind="agents"
      data-placement={placement}
      data-width={width}
      aria-hidden={ariaHidden}
    >
      <div className="cmd-suggestions" role="listbox">
        {items.map((item, i) => {
          const isCurrent = item.id === currentId;
          const isActive = activeIndex === i;
          const cls = [
            "cmd-suggestion",
            isActive && "cmd-suggestion--active",
            isCurrent && "cmd-suggestion--current",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={isActive}
              tabIndex={open ? 0 : -1}
              className={cls}
              onMouseEnter={() => onHover?.(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(item.id)}
            >
              <span className="cmd-suggestion-current-marker" aria-hidden="true">·</span>
              <span className="cmd-suggestion-label">{item.label}</span>
              {item.description && (
                <span className="cmd-suggestion-desc">{item.description}</span>
              )}
            </button>
          );
        })}
      </div>
      {footer && (
        <button
          type="button"
          className="cmd-suggestions-footer"
          tabIndex={open ? 0 : -1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={footer.onClick}
        >
          <span>{footer.label}</span>
          <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" width="14" height="14">
            <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>
  );
}
